import { clientAgentId, techAgentId } from 'constants/history';
import { AgentType } from 'enums/AgentType';
import { CustomMessageType } from 'enums/CustomMessageType';
import { HistoryDumpFormat } from 'enums/HistoryDumpFormat';
import { HistoryRequestType } from 'enums/HistoryRequestType';
import { TransmitStatus } from 'enums/TransmitStatus';
import { HistoryData } from 'interfaces/HistoryData';
import { InformationBanner } from 'interfaces/InformationBanner';
import { ILastAgent } from 'interfaces/LastAgent';
import { MessageData, MessageReaction, RenderMessageData } from 'interfaces/MessageData';
import { UnreadReaction } from 'interfaces/UnreadReactions';
import { getNetworkTime } from 'utils/dateTime';
import { wsToChatMessage } from 'utils/messages';
import { randomUuid, typedRandomUuuid } from 'utils/random';

export const enum HistoryActionType {
    Initialised = 'HISTORY/INITIALISED',
    SetOperatorAvailable = 'HISTORY/SET_OPERATOR_AVAILABLE',
    RequestHistory = 'HISTORY/REQUEST',
    RequestReactions = 'HISTORY/REACTIONS_REQUEST',
    ReactionRead = 'HISTORY/REACTION_READ',
    SetUndreadReactions = 'HISTORY/SET_UNREAD_REACTION',
    ReceiveHistory = 'HISTORY/RECEIVE',
    SetHistoryBeginning = 'HISTORY/SET_HISTORY_BEGINNING',
    SetTextSelectionMessageId = 'HISTORY/SET_TEXT_SELECTION_MESSAGE_ID',
    SetHistoryEnd = 'HISTORY/SET_HISTORY_END',
    ReceiveMessage = 'HISTORY/RECEIVE_MESSAGE',
    ReplyMessage = 'HISTORY/REPLY_MESSAGE',
    MarkMessageReceived = 'HISTORY/MARK_MESSAGE_RECEIVED',
    MarkMessageRead = 'HISTORY/MARK_MESSAGE_READ',
    SendMessageReceived = 'HISTORY/SEND_MESSAGE_RECEIVED',
    SendMessageRead = 'HISTORY/SEND_MESSAGE_READ',
    SendMessage = 'HISTORY/SEND_MESSAGE',
    SetLastMessageSentTimestamp = 'HISTORY/SET_LAST_MESSAGE_SENT_TIMESTAMP',
    AddClientSideMessage = 'HISTORY/ADD_CLIENTSIDE_MESSAGE',
    DeleteClientSideMessage = 'HISTORY/DELETE_CLIENTSIDE_MESSAGE',
    SendMessageTimeout = 'HISTORY/SEND_MESSAGE_TIMEOUT',
    LoadPendingMessages = 'HISTORY/LOAD_PENDING_MESSAGES',
    PendingMessagesLoaded = 'HISTORY/PENDING_MESSAGES_LOADED',
    FocusMessage = 'HISTORY/FOCUS_MESSAGE',
    GoToUnreadReaction = 'HISTORY/GO_TO_UNREAD_REACTION',
    MessageFocused = 'HISTORY/MESSAGE_FOCUSED',
    SetVisibleRange = 'HISTORY/SET_VISIBLE_RANGE',
    GoToBottom = 'HISTORY/GO_TO_BOTTOM',
    MarkMessageFavorite = 'HISTORY/MARK_MESSAGE_FAVORITE',
    UnmarkMessageFavorite = 'HISTORY/UNMARK_MESSAGE_FAVORITE',
    Clear = 'HISTORY/CLEAR',
    ProcessDeeplinkScenario = 'HISTORY/PROCESS_DEEPLINK_SCENARIO',
    ProcessDeeplinkLocalScenario = 'HISTORY/PROCESS_DEEPLINK_LOCAL_SCENARIO',
    Dump = 'HISTORY/DUMP',
    SetFirstUnreadMsgId = 'HISTORY/SET_FIRST_UNREAD_MSG_ID',
    SetLastAgent = 'HISTORY/SET_LAST_AGENT',
    ClearLastAgent = 'HISTORY/CLEAR_LAST_AGENT',
    FlushQueuedHistoryActions = 'HISTORY/FLUSH_QUEUED_HISTORY_ACTIONS',
    FlushQueuedScenarios = 'HISTORY/FLUSH_QUEUED_SCENARIOS',
    SetFavoritesMode = 'HISTORY/SET_FAVORITES_MODE',
    AddMessageReaction = 'HISTORY/ADD_MESSAGE_REACTION',
    RemoveMessageReaction = 'HISTORY/REMOVE_MESSAGE_REACTION',
    SetMessageReactions = 'HISTORY/SET_MESSAGE_REACTIONS',
    LookFollowedMessage = 'HISTORY/LOOK_FOLLOWED_MESSAGE',
    PrependFollowedMessage = 'HISTORY/PREPEND_FOLLOWED_MESSAGE',
    PruneFollowedMessages = 'HISTORY/PRUNE_FOLLOWED_MESSAGES',
    SetOperatorAwaitTimeMs = 'HISTORY/SET_OPERATOR_AWAIT_TIME',
    SetAppealClosedBy = 'HISTORY/SET_APPEAL_CLOSED_BY',
    SetMessagesReceivedOnlineIdList = 'HISTORY/SET_MESSAGES_RECEIVED_ONLINE_IDS',
}

export type InitialisedAction = ReturnType<typeof setInitialised>;
export const setInitialised = (initialised: boolean) => ({
    type: HistoryActionType.Initialised,
    payload: initialised,
});

export type SetOperatorAvailableAction = ReturnType<typeof setOperatorAvailable>;
export const setOperatorAvailable = (operatorAvailable: boolean, chatNow: boolean) => ({
    type: HistoryActionType.SetOperatorAvailable,
    payload: { operatorAvailable, chatNow },
});

export type RequestHistoryAction = ReturnType<typeof requestHistory>;
export const requestHistory = (
    type: HistoryRequestType = HistoryRequestType.Older,
    msgId?: string,
    increasedChunk?: boolean,
) => ({
    type: HistoryActionType.RequestHistory,
    payload: {
        type,
        msgId,
        increasedChunk,
    },
});

export type RequestUnreadReactionsAction = ReturnType<typeof requestUnreadReactions>;
export const requestUnreadReactions = () => ({
    type: HistoryActionType.RequestReactions,
});

export type ReactionReadAction = ReturnType<typeof reactionRead>;
export const reactionRead = (msgId: string) => ({
    type: HistoryActionType.ReactionRead,
    payload: msgId,
});

export type SetUnreadReactionsAction = ReturnType<typeof setUnreadReactions>;
export const setUnreadReactions = (payload: Array<UnreadReaction>) => ({
    type: HistoryActionType.SetUndreadReactions,
    payload,
});

export type GoToUnreadReactionAction = ReturnType<typeof goToUnreadReaction>;
export const goToUnreadReaction = (msgId: string) => ({
    type: HistoryActionType.GoToUnreadReaction,
    payload: msgId,
});

export type ReceiveHistoryAction = ReturnType<typeof receiveHistory>;
export const receiveHistory = (history: HistoryData) => ({
    type: HistoryActionType.ReceiveHistory,
    payload: { history, timestamp: new Date() },
});

export type SetTextSelectionMessageIdAction = ReturnType<typeof setTextSelectionMessageId>;
export const setTextSelectionMessageId = (messageId: string | null) => ({
    type: HistoryActionType.SetTextSelectionMessageId,
    payload: { messageId },
});

export type SetHistoryBeginningAction = ReturnType<typeof setHistoryBeginning>;
export const setHistoryBeginning = (msgId: string) => ({
    type: HistoryActionType.SetHistoryBeginning,
    payload: msgId,
});

export type SetHistoryEndAction = ReturnType<typeof setHistoryEnd>;
export const setHistoryEnd = (msgId: string) => ({
    type: HistoryActionType.SetHistoryEnd,
    payload: msgId,
});

export type ReceiveMessageAction = ReturnType<typeof receiveMessage>;
export const receiveMessage = (msg: MessageData) => ({
    type: HistoryActionType.ReceiveMessage,
    payload: msg,
});

export type ReplyMessageAction = ReturnType<typeof replyMessage>;
export const replyMessage = (msg: MessageData) => ({
    type: HistoryActionType.ReplyMessage,
    payload: msg,
});

export type AddMessageReactionAction = ReturnType<typeof addMessageReaction>;
export const addMessageReaction = (payload: {
    messageId: number | string;
    reactionId: number;
    clientId?: number;
}) => ({
    type: HistoryActionType.AddMessageReaction,
    payload,
});

export type RemoveMessageReactionAction = ReturnType<typeof removeMessageReaction>;
export const removeMessageReaction = (payload: { messageId: number | string }) => ({
    type: HistoryActionType.RemoveMessageReaction,
    payload: payload,
});

export type SetMessageReactionsAction = ReturnType<typeof setMessageReactions>;
export const setMessageReactions = (messageId: number | string, reactions?: MessageReaction[]) => ({
    type: HistoryActionType.SetMessageReactions,
    payload: { messageId, reactions },
});

export type MarkMessageReceivedAction = ReturnType<typeof markMessageReceived>;
export const markMessageReceived = (msgId: string) => ({
    type: HistoryActionType.MarkMessageReceived,
    payload: msgId,
});

export type MarkMessageReadAction = ReturnType<typeof markMessageRead>;
export const markMessageRead = (msgId: string) => ({
    type: HistoryActionType.MarkMessageRead,
    payload: msgId,
});

export type SendMessageReceivedAction = ReturnType<typeof sendMessageReceived>;
export const sendMessageReceived = (msgId: string) => ({
    type: HistoryActionType.SendMessageReceived,
    payload: msgId,
});

export type SendMessageReadAction = ReturnType<typeof sendMessageRead>;
export const sendMessageRead = (msgId: string) => ({
    type: HistoryActionType.SendMessageRead,
    payload: msgId,
});

export type SendMessageAction = ReturnType<typeof sendMessage>;
export const sendMessage = (msg: MessageData) => {
    const uuid = msg.uuid ?? randomUuid();
    return {
        type: HistoryActionType.SendMessage,
        payload: {
            ...msg,
            id: uuid,
            agentId: clientAgentId,
            time: Math.floor(getNetworkTime() / 1000),
            uuid,
            status: TransmitStatus.Pending,
        },
    };
};

export type SetLastMessageSentTimestampAction = ReturnType<typeof setLastMessageSentTimestamp>;
export const setLastMessageSentTimestamp = (timestamp: Date) => ({
    type: HistoryActionType.SetLastMessageSentTimestamp,
    payload: timestamp,
});

export type AddClientSideMessageAction = ReturnType<typeof addClientSideMessage>;
export const addClientSideMessage = (msg: MessageData, smoothScroll = true) => {
    return {
        type: HistoryActionType.AddClientSideMessage,
        payload: {
            message: {
                ...msg,
                clientSide: true,
            },
            smoothScroll,
        },
    };
};

export const addCustomMessage = (
    type: CustomMessageType,
    text?: string,
    smoothScroll = true,
    agent_id = techAgentId,
) => {
    const uuid = `[${type}]${randomUuid()}`;

    return addClientSideMessage(
        wsToChatMessage({
            id: uuid,
            agent_id,
            customType: type,
            data: {
                [uuid]: {
                    text,
                    uuid,
                    time: getNetworkTime() / 1000,
                },
            },
        }),
        smoothScroll,
    );
};

export const addInformationBannerMessage = (
    informationBanner: InformationBanner,
    customUuid?: string,
) => {
    const type = CustomMessageType.InformationBanner;
    const uuid = customUuid ?? typedRandomUuuid(type);

    return addClientSideMessage(
        wsToChatMessage({
            id: uuid,
            agent_id: techAgentId,
            customType: type,
            data: {
                [uuid]: {
                    text: informationBanner.text,
                    metadata: {
                        informationBanner: {
                            header: informationBanner.header,
                            text: informationBanner.text,
                        },
                    },
                    uuid,
                    time: getNetworkTime() / 1000,
                },
            },
        }),
        false,
    );
};

export type DeleteClientSideMessageAction = ReturnType<typeof deleteClientSideMessage>;
export const deleteClientSideMessage = (uuid: string) => ({
    type: HistoryActionType.DeleteClientSideMessage,
    payload: uuid,
});

export type SendMessageTimeoutAction = ReturnType<typeof sendMessageTimeout>;
export const sendMessageTimeout = (uuid: string) => ({
    type: HistoryActionType.SendMessageTimeout,
    payload: uuid,
});

export type LoadPendingMessagesAction = ReturnType<typeof loadPendingMessages>;
export const loadPendingMessages = () => ({
    type: HistoryActionType.LoadPendingMessages,
});

export type PendingMessagesLoadedAction = ReturnType<typeof pendingMessagesLoaded>;
export const pendingMessagesLoaded = (pendingMessages: { [uuid: string]: RenderMessageData }) => ({
    type: HistoryActionType.PendingMessagesLoaded,
    payload: pendingMessages,
});

export type FocusMessageAction = ReturnType<typeof focusMessage>;
export const focusMessage = (msgId: string, smooth = false) => ({
    type: HistoryActionType.FocusMessage,
    payload: { msgId, smooth },
});

export type MessageFocusedAction = ReturnType<typeof messageFocused>;
export const messageFocused = () => ({
    type: HistoryActionType.MessageFocused,
});

export type SetVisibleHistoryRangeAction = ReturnType<typeof setVisibleHistoryRange>;
export const setVisibleHistoryRange = (
    firstVisibleId: string,
    lastVisibleId: string,
    msgList?: HTMLDivElement,
) => ({
    type: HistoryActionType.SetVisibleRange,
    payload: { visibleRange: [firstVisibleId, lastVisibleId] as const, msgList },
});

export type GoToBottomAction = ReturnType<typeof goToBottom>;
export const goToBottom = (smooth = false) => ({
    type: HistoryActionType.GoToBottom,
    payload: smooth,
});

export type MarkMessageFavoriteAction = ReturnType<typeof markMessageFavorite>;
export const markMessageFavorite = (msgId: string, predicted = false) => ({
    type: HistoryActionType.MarkMessageFavorite,
    payload: { msgId, predicted },
});

export type UnmarkMessageFavoriteAction = ReturnType<typeof unmarkMessageFavorite>;
export const unmarkMessageFavorite = (msgId: string, predicted = false) => ({
    type: HistoryActionType.UnmarkMessageFavorite,
    payload: { msgId, predicted },
});

export type ClearHistoryAction = ReturnType<typeof clearHistory>;
export const clearHistory = () => ({
    type: HistoryActionType.Clear,
});

export type ProcessScenarioAction = ReturnType<typeof processScenario>;
export const processScenario = ({
    scenarioId,
    isLocalScenario,
}: {
    scenarioId: string;
    isLocalScenario: boolean;
}) => ({
    type: isLocalScenario
        ? HistoryActionType.ProcessDeeplinkLocalScenario
        : HistoryActionType.ProcessDeeplinkScenario,
    payload: scenarioId,
});

export type DumpHistoryAction = ReturnType<typeof dumpHistory>;
export const dumpHistory = (format: HistoryDumpFormat) => ({
    type: HistoryActionType.Dump,
    payload: { format },
});

export type SetFirstUnreadMsgIdAction = ReturnType<typeof setFirstUnreadMsgId>;
export const setFirstUnreadMsgId = (msgId: string) => ({
    type: HistoryActionType.SetFirstUnreadMsgId,
    payload: msgId,
});

export type SetLastAgentAction = ReturnType<typeof setLastAgent>;
export const setLastAgent = (id: string, type: AgentType) => ({
    type: HistoryActionType.SetLastAgent,
    payload: { id, type },
});

export type ClearLastAgentAction = ReturnType<typeof clearLastAgent>;
export const clearLastAgent = () => ({
    type: HistoryActionType.ClearLastAgent,
});

export type SetAppealClosedBy = ReturnType<typeof setAppealClosedBy>;
export const setAppealClosedBy = (lastAgent?: ILastAgent) => ({
    type: HistoryActionType.SetAppealClosedBy,
    payload: lastAgent,
});

export type FlushQueuedHistoryActionsAction = ReturnType<typeof flushQueuedHistoryActions>;
export const flushQueuedHistoryActions = () => ({
    type: HistoryActionType.FlushQueuedHistoryActions,
});

export type FlushQueuedScenariosAction = ReturnType<typeof flushQueuedScenariosAction>;
export const flushQueuedScenariosAction = () => ({
    type: HistoryActionType.FlushQueuedScenarios,
});

export type SetFavoritesModeAction = ReturnType<typeof setFavoritesMode>;
export const setFavoritesMode = (favorites: boolean) => ({
    type: HistoryActionType.SetFavoritesMode,
    payload: favorites,
});

export type LookFollowedMessageAction = ReturnType<typeof lookFollowedMessage>;
export const lookFollowedMessage = (smooth = false) => ({
    type: HistoryActionType.LookFollowedMessage,
    payload: smooth,
});

export type PrependFollowedMessageAction = ReturnType<typeof prependFollowedMessage>;
export const prependFollowedMessage = (messageId: string | number) => ({
    type: HistoryActionType.PrependFollowedMessage,
    payload: String(messageId),
});

export type PruneFollowedMessagesAction = ReturnType<typeof pruneFollowedMessages>;
export const pruneFollowedMessages = (end: number) => ({
    type: HistoryActionType.PruneFollowedMessages,
    payload: { end },
});

export type SetOperatorAwaitTimeAction = ReturnType<typeof setOperatorAwaitTimeMs>;
export const setOperatorAwaitTimeMs = (operatorAwaitTimeMs: number | null) => ({
    type: HistoryActionType.SetOperatorAwaitTimeMs,
    payload: operatorAwaitTimeMs,
});

export type SetMessagesReceivedOnlineIdListAction = ReturnType<
    typeof setMessagesReceivedOnlineIdList
>;
export const setMessagesReceivedOnlineIdList = (idList: string[]) => ({
    type: HistoryActionType.SetMessagesReceivedOnlineIdList,
    payload: idList,
});
